import * as React from 'react';
import { clsx } from 'clsx';
import { Popover, PopoverPlacement, PopoverClose } from '../overlay/Popover';

export interface InfoClickPopoverProps {
  content: React.ReactNode;
  title?: React.ReactNode;
  placement?: PopoverPlacement;
  iconSize?: 'sm' | 'md' | 'lg';
  className?: string;
  contentClassName?: string;
  ariaLabel?: string;
  showCloseButton?: boolean;
}

const iconSizes = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

// Click-triggered info popover. Distinct from `InfoTooltip` (hover, glanceable)
// and `InfoPopover` from overlay/ (also hover). Use this when the popover
// content is multi-line, scrollable, or interactive — the user must be able
// to move their cursor into it without the panel dismissing.
export function InfoClickPopover({
  content,
  title,
  placement = 'bottom',
  iconSize = 'md',
  className,
  contentClassName,
  ariaLabel = 'More information',
  showCloseButton = true,
}: InfoClickPopoverProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Restore focus to the trigger after close so keyboard users land back on
  // a sensible element. The Popover handles Escape + outside-click for us.
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      triggerRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement={placement}
      trigger="click"
      closeOnClickOutside
      closeOnEscape
      showArrow
      contentClassName={contentClassName}
      content={
        <div className="relative p-3">
          {showCloseButton && <PopoverClose onClose={() => setIsOpen(false)} />}
          {title && (
            <h4 className="font-medium text-gray-900 dark:text-white mb-2 text-sm pr-6">
              {title}
            </h4>
          )}
          <div className="text-sm text-gray-700 dark:text-gray-300">{content}</div>
        </div>
      }
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className={clsx(
          'inline-flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full',
          className
        )}
      >
        <svg
          className={iconSizes[iconSize]}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
    </Popover>
  );
}
